import React from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Plus, Trash } from "@phosphor-icons/react";

/**
 * SupportingTendersEditor
 * -----------------------
 * Reusable free-form "supporting tenders" list — a description-style
 * textarea per entry. Used on Pending Works, Sales Invoice, Temp
 * Invoice, and any other doc that needs to reference related /
 * past tenders without a structured schema.
 *
 * Props:
 *   value    : string[]     — current list
 *   onChange : (string[]) → void
 *   testIdPrefix (optional) — prefix for data-testids
 *   compact  (optional)     — smaller header text
 *   showHeader (optional)   — hide the header row when false (default true)
 */
export function SupportingTendersEditor({
  value,
  onChange,
  testIdPrefix = "supporting-tender",
  showHeader = true,
}) {
  const items = value || [];
  const update = (i, val) => onChange(items.map((s, idx) => (idx === i ? val : s)));
  const add = () => onChange([...items, ""]);
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));

  // Hide the whole block entirely when there's nothing to show AND the
  // consumer isn't rendering their own Add button (i.e. showHeader=false).
  if (items.length === 0 && !showHeader) return null;

  return (
    <div className="space-y-3" data-testid={testIdPrefix}>
      {showHeader && (
        <div className="flex items-center justify-between gap-2">
          <Label className="eyebrow">Supporting Company Name</Label>
          <Button type="button" size="sm" variant="outline" onClick={add}
                  data-testid={`${testIdPrefix}-add`}>
            <Plus size={13} className="mr-1" /> Add supporting company name
          </Button>
        </div>
      )}

      {items.length > 0 && (
        <div className="space-y-2">
          {items.map((val, i) => (
            <div key={i} className="rounded-md border border-primary/30 bg-primary/[.04] p-3 space-y-2"
                 data-testid={`${testIdPrefix}-card-${i}`}>
              <div className="flex items-center justify-between">
                <div className="eyebrow" style={{ color: "hsl(var(--primary))" }}>Supporting Company {i + 1}</div>
                <Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-destructive"
                        onClick={() => remove(i)}
                        data-testid={`${testIdPrefix}-remove-${i}`}>
                  <Trash size={13} />
                </Button>
              </div>
              <Input
                value={val}
                onChange={(e) => update(i, e.target.value)}
                placeholder={`Supporting company name ${i + 1}`}
                data-testid={`${testIdPrefix}-text-${i}`}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * SupportingTendersView — read-only variant for print / public share pages.
 * Renders each supporting tender as a numbered block. Returns null when empty.
 */
export function SupportingTendersView({ items }) {
  const list = items || [];
  if (!list.length) return null;
  return (
    <div className="mt-6" data-testid="supporting-tenders-view">
      <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-2"
           style={{ fontFamily: "'Chivo', sans-serif" }}>
        Supporting Company Name
      </div>
      <div className="space-y-1">
        {list.map((s, i) => (
          <div key={i} className="text-[12px] border-l-2 border-primary/40 pl-3">
            <span className="font-semibold">Company {i + 1}: </span>{s}
          </div>
        ))}
      </div>
    </div>
  );
}
